'use client';
import React, { useEffect, useState, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { createClient } from '@/lib/supabase/client';
import { Search, Download, Loader2 } from 'lucide-react';

interface Language { id: string; name: string; }
interface TextResult {
  id: string;
  original_text: string;
  english_translation: string;
  dialect: string;
  location: string;
  category: string;
  submission_status: string;
  created_at: string;
  mutauro_languages?: { name: string };
}

const STATUS_BADGE: Record<string, { label: string; color: string; bg: string }> = {
  human_approved: { label: 'Verified', color: '#1A6B3A', bg: '#1A6B3A18' },
  pending:        { label: 'Pending',  color: '#CC7700', bg: '#CC770018' },
  ai_reviewed:    { label: 'AI Reviewed', color: '#007070', bg: '#00707018' },
  flagged:        { label: 'Flagged',  color: '#CC5500', bg: '#CC550018' },
  human_rejected: { label: 'Rejected', color: '#CC0000', bg: '#CC000018' },
};

export default function MutauroSearchPage() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<TextResult[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [filterLang, setFilterLang] = useState('');
  const [filterStatus, setFilterStatus] = useState('human_approved');
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;
  const supabase = createClient();

  useEffect(() => {
    supabase.from('mutauro_languages').select('id,name').order('name').then(({ data }) => { if (data) setLanguages(data); });
  }, []);

  const doSearch = useCallback(async (q: string, lang: string, status: string, pg: number) => {
    setLoading(true);
    try {
      let qb = supabase
        .from('mutauro_text_submissions')
        .select('id,original_text,english_translation,dialect,location,category,submission_status,created_at,mutauro_languages(name)', { count: 'exact' });

      if (status) qb = qb.eq('submission_status', status);
      if (lang) qb = qb.eq('language_id', lang);
      if (q.trim()) {
        qb = qb.or(`original_text.ilike.%${q}%,english_translation.ilike.%${q}%`);
      }

      const { data, count } = await qb
        .order('created_at', { ascending: false })
        .range(pg * PAGE_SIZE, (pg + 1) * PAGE_SIZE - 1);

      setResults(data || []);
      setTotal(count || 0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => doSearch(query, filterLang, filterStatus, page), 300);
    return () => clearTimeout(t);
  }, [query, filterLang, filterStatus, page, doSearch]);

  function exportCSV() {
    const rows = [['ID', 'Language', 'Original', 'English', 'Dialect', 'Location', 'Category', 'Status', 'Date']];
    results.forEach(r => rows.push([
      r.id, r.mutauro_languages?.name || '', r.original_text || '',
      r.english_translation || '', r.dialect || '', r.location || '',
      r.category || '', r.submission_status, r.created_at?.slice(0, 10),
    ]));
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `mutauro-dataset-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  const inputCls = "px-3 py-2 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-red-200 transition-all";
  const inputStyle = { borderColor: 'var(--border)', background: 'var(--input)', color: 'var(--foreground)' };

  return (
    <AppLayout>
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-mono uppercase tracking-widest px-2 py-0.5 rounded"
              style={{ background: '#CC000018', color: '#CC0000', fontFamily: 'var(--font-mono)' }}>Mutauro AI</span>
          </div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--foreground)' }}>Dataset Search</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>
            Search across Zimbabwe's indigenous language dataset.
          </p>
        </div>

        {/* Search + Filters */}
        <div className="flex flex-wrap gap-3 mb-5">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
            <input
              className={`${inputCls} w-full pl-9`} style={inputStyle}
              placeholder="Search words, phrases, translations…"
              value={query} onChange={e => { setQuery(e.target.value); setPage(0); }} />
          </div>
          <select className={inputCls} style={inputStyle} value={filterLang}
            onChange={e => { setFilterLang(e.target.value); setPage(0); }}>
            <option value="">All Languages</option>
            {languages.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
          <select className={inputCls} style={inputStyle} value={filterStatus}
            onChange={e => { setFilterStatus(e.target.value); setPage(0); }}>
            <option value="">All Statuses</option>
            <option value="human_approved">Verified Only</option>
            <option value="pending">Pending</option>
            <option value="ai_reviewed">AI Reviewed</option>
          </select>
          <button onClick={exportCSV}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors"
            style={{ borderColor: 'var(--border)', background: 'var(--card)', color: 'var(--foreground)' }}>
            <Download size={14} /> Export CSV
          </button>
        </div>

        {/* Results count */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
            {loading ? 'Searching…' : `${total.toLocaleString()} records found`}
          </span>
          {total > PAGE_SIZE && (
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0 || loading}
                className="px-3 py-1 rounded border text-xs disabled:opacity-40"
                style={{ borderColor: 'var(--border)', background: 'var(--card)', color: 'var(--foreground)' }}>
                Prev
              </button>
              <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Page {page + 1} of {Math.ceil(total / PAGE_SIZE)}</span>
              <button onClick={() => setPage(p => p + 1)} disabled={(page + 1) * PAGE_SIZE >= total || loading}
                className="px-3 py-1 rounded border text-xs disabled:opacity-40"
                style={{ borderColor: 'var(--border)', background: 'var(--card)', color: 'var(--foreground)' }}>
                Next
              </button>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="rounded-xl border overflow-hidden" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={24} className="animate-spin" style={{ color: '#CC0000' }} />
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-16">
              <Search size={28} className="mx-auto mb-3" style={{ color: 'var(--muted-foreground)' }} />
              <div className="text-sm" style={{ color: 'var(--muted-foreground)' }}>No records found. Try a different search or filter.</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: 'var(--secondary)', borderBottom: '1px solid var(--border)' }}>
                    {['Language', 'Original', 'English', 'Dialect', 'Category', 'Status'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide"
                        style={{ color: 'var(--muted-foreground)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {results.map((r, i) => {
                    const sb = STATUS_BADGE[r.submission_status] || STATUS_BADGE.pending;
                    return (
                      <tr key={r.id} style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'transparent' : 'var(--secondary)' }}>
                        <td className="px-4 py-3 font-medium" style={{ color: 'var(--foreground)' }}>
                          {r.mutauro_languages?.name || '—'}
                        </td>
                        <td className="px-4 py-3 max-w-xs">
                          <span className="line-clamp-2" style={{ color: 'var(--foreground)' }}>{r.original_text}</span>
                        </td>
                        <td className="px-4 py-3 max-w-xs">
                          <span className="line-clamp-2" style={{ color: 'var(--muted-foreground)' }}>{r.english_translation || '—'}</span>
                        </td>
                        <td className="px-4 py-3" style={{ color: 'var(--muted-foreground)' }}>{r.dialect || '—'}</td>
                        <td className="px-4 py-3">
                          <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}>
                            {r.category?.replace(/_/g, ' ') || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: sb.bg, color: sb.color }}>
                            {sb.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
